import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  price,
  sectionSubtitle,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';
import {
  BB_FAMILY_ID,
  BB_FAMILY_NAME,
  BB_THEME,
  BB_TORN,
  BB_VARIANTS,
  bbBannerCss,
  bbHeadHeight,
  bbHeaderCss,
  bbPhotoCss,
  bbRootCss,
} from './brush-burger-board.js';

/**
 * Brush Burger, layout B: the same grunge board as photo cards. Each section opens with a torn
 * brush banner over a row of cards: a torn round photo on an accent ring with the price painted
 * across its foot, the name in distressed capitals, a dashed rule and the description.
 */

const S = '.mb-t-brush-burger-cards';
const TITLE_EM = 6.2;
const PHOTO = 12;
const PHOTO_PORTRAIT = 13;
/** Bilingual cards carry two more lines, so landscape draws them smaller. */
const PHOTO_BOTH = 10;
const BOTH_SCALE = 0.9;

function perRow(portrait: boolean): number {
  return portrait ? 3 : 5;
}

/** Height of one row of cards in em, matching the CSS. */
function cardHeight(board: MenuBoardContent, portrait: boolean): number {
  const both = board.settings.language === 'both';
  const photo = portrait ? PHOTO_PORTRAIT : both ? PHOTO_BOTH : PHOTO;
  const name = 2 * 1.1 + (both ? 1.3 * 1.3 * ARABIC_LINE : 0);
  const desc = board.settings.showDescriptions ? (both ? 1 * 1.3 + 1 * 1.3 * ARABIC_LINE : 2 * 1.3) : 0;
  return 0.6 + photo + 1.4 + name + 1.05 + desc + 1;
}

function headerHeight(board: MenuBoardContent): number {
  const both = board.settings.language === 'both';
  // Brand and title sit side by side.
  const brand = board.brand.name || board.brand.nameAr ? 2.8 * 1.35 + (both && board.brand.nameAr ? 1.8 * 1.3 * 1.2 : 0) : 0;
  const title = TITLE_EM * 0.92 + 0.3 + (both && board.settings.titleAr ? TITLE_EM * 0.45 * 1.3 * ARABIC_LINE : 0);
  return Math.max(brand, title) + 1.4;
}

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const rows = perRow(portrait);
  const card = cardHeight(board, portrait);
  const scale = !portrait && board.settings.language === 'both' ? BOTH_SCALE : 1;
  return {
    capacity: (portrait ? 120 : 67.5) - 3.5 - headerHeight(board),
    blockCost: (section, items, continued) =>
      scale * (bbHeadHeight(board, section, continued) + Math.ceil(items.length / rows) * card + 0.6),
  };
}

function cardPrice(ctx: RenderContext, item: MenuItemContent): string {
  if (!item.variants.length) return sellingPrice(ctx, item, 'bb2-price');
  return item.variants
    .map((v) => `<span class="bb2-price"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join('<span class="bb2-slash">/</span>');
}

function card(ctx: RenderContext, item: MenuItemContent): string {
  const photo = image(ctx, item.imageMediaId, 'bb2-photo-img');
  const initial = escapeHtml((item.title.trim()[0] ?? '?').toUpperCase());
  const badges = badge(ctx, item, 'bb2-badge');
  return [
    '<div class="bb2-card">',
    `<div class="bb2-photo${photo ? '' : ' bb2-nophoto'}">${photo || `<span class="bb2-initial">${initial}</span>`}`,
    badges ? `<span class="bb2-badges">${badges}</span>` : '',
    `<span class="bb2-chip">${cardPrice(ctx, item)}</span></div>`,
    bilingual(ctx, item.title, item.titleAr, 'bb2-name'),
    `<div class="bb2-rule"></div>`,
    description(ctx, item, 'bb2-desc'),
    '</div>',
  ].join('');
}

function section(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section) ? `<div class="bb2-banner">${sectionTitle(ctx, b.section, 'bb2-bt')}</div>` : '';
  const note = b.continued ? '' : sectionSubtitle(ctx, b.section, 'bb2-note');
  return `<div class="bb2-sec">${head}${note}<div class="bb2-cards">${b.items.map((i) => card(ctx, i)).join('')}</div></div>`;
}

export const brushBurgerCards: MenuTemplate = {
  id: 'brush-burger-cards',
  name: 'Brush photo cards',
  description:
    'Grunge black burger board of photo cards: torn round photos with the price painted across, names in distressed capitals and brush-stroke section banners.',
  defaultTheme: BB_THEME,
  variants: BB_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { id: BB_FAMILY_ID, name: BB_FAMILY_NAME, layout: 'Photo cards' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    // One tall column of sections; each section lays its cards out in rows.
    return packColumns(groupBySection(board), [column(board, orientation === 'portrait')]);
  },

  render(ctx) {
    const body = ctx.page.columns.flat().map((b) => section(ctx, b)).join('');
    const both = ctx.showEn && ctx.showAr ? ' bb2-both' : '';
    const { board } = ctx;
    const title = board.settings.title ?? board.name;
    const brand = bilingual(ctx, board.brand.name, board.brand.nameAr, 'bb2-brand');
    const header = [
      '<div class="bb2-header">',
      brand ? `<div class="bb2-brandbox">${brand}</div>` : '',
      `<div class="bb2-titles" style="font-size:${fitScale(title, brand ? 15 : 22, 0.5).toFixed(3)}em">`,
      bilingual(ctx, title, board.settings.titleAr, 'bb2-title'),
      '</div></div>',
    ].join('');
    return `${header}<div class="bb2-body${both}">${body}</div>`;
  },

  css: `
${bbRootCss(S)}
${S}{padding:2em 3em 1.5em}
${bbHeaderCss(S, 'bb2', TITLE_EM)}
${S} .bb2-header{display:flex;align-items:center;justify-content:center;gap:1.4em;margin-bottom:1.4em;min-width:0}
${S} .bb2-brandbox{flex:0 1 auto;min-width:0;max-width:40%}
${S} .bb2-brandbox .bb2-brand{text-align:right}
${S} .bb2-brand{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:0 .2em}
${S} .bb2-titles{flex:0 1 auto;min-width:0;text-align:start}
${S} .bb2-title{-webkit-line-clamp:1;white-space:nowrap}
${S} .bb2-body{flex:1;min-height:0;display:flex;flex-direction:column}
${bbBannerCss(S, 'bb2-banner')}
${S} .bb2-sec{display:flex;flex-direction:column;margin-bottom:.6em}
${S} .bb2-banner{align-self:center;min-width:26em;max-width:100%;padding:.8em 2.6em .75em;margin-bottom:1.2em;text-align:center}
${S} .bb2-bt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.1em;line-height:1.1;letter-spacing:.06em;
  text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .bb2-bt.mb-2nd{font-size:1.35em;letter-spacing:0}
${S} .bb2-note{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:1.3em;text-align:center;margin:-.4em 0 .6em}
${S} .bb2-cards{display:flex;flex-wrap:wrap;justify-content:center;column-gap:2em}
${S}.mb-portrait .bb2-cards{column-gap:1.6em}
${S} .bb2-card{flex:0 0 calc((100% - 8em) / 5)}
${S}.mb-portrait .bb2-card{flex-basis:calc((100% - 3.2em) / 3)}
${S} .bb2-card{min-width:0;display:flex;flex-direction:column;align-items:center;text-align:center;padding:.6em .8em 1em}
${bbPhotoCss(S, 'bb2-photo')}
${S} .bb2-photo{width:${PHOTO}em;margin-bottom:1.4em}
${S}.mb-portrait .bb2-photo{width:${PHOTO_PORTRAIT}em}
${S} .bb2-nophoto::after{content:'';position:absolute;inset:0;background:var(--mb-surface);
  -webkit-mask:${BB_TORN} center/100% 100% no-repeat;mask:${BB_TORN} center/100% 100% no-repeat}
${S} .bb2-initial{position:absolute;inset:0;z-index:1;display:flex;align-items:center;justify-content:center;
  font-family:var(--mb-font-heading);font-size:4.5em;color:var(--mb-accent)}
${bbBannerCss(S, 'bb2-chip')}
${S} .bb2-chip{position:absolute;z-index:2;left:50%;bottom:-.9em;transform:translateX(-50%) rotate(-4deg);white-space:nowrap;
  display:flex;align-items:baseline;gap:.3em;padding:.25em 1.1em .2em;background:var(--mb-accent);color:var(--mb-bg)}
${S} .bb2-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.7em;line-height:1.1;letter-spacing:.04em}
${S} .bb2-price small{font-size:.55em;letter-spacing:.06em}
${S} .bb2-price-old{font-size:.9em;opacity:.65}
${S} .bb2-slash{opacity:.5;font-size:1.2em}
${S} .bb2-badges{position:absolute;z-index:2;top:.2em;left:-.4em;display:flex;flex-direction:column;align-items:flex-start;gap:.3em}
${S} .bb2-badge{font-size:.85em;font-weight:800;padding:.2em .6em;border-radius:.2em;background:var(--mb-primary);color:var(--mb-bg);transform:rotate(-8deg);white-space:nowrap}
${S} .bb2-name{max-width:100%;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;line-height:1.1;letter-spacing:.05em;
  text-transform:uppercase;color:var(--mb-accent);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .bb2-name.mb-2nd{font-size:1.3em;letter-spacing:0;opacity:.9}
${S} .bb2-rule{width:80%;margin:.35em 0 .55em;border-top:.12em dashed color-mix(in srgb,var(--mb-text) 55%,transparent)}
${S} .bb2-desc{max-width:100%;font-size:1em;line-height:1.3;opacity:.92;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .bb2-both .bb2-desc{-webkit-line-clamp:1}
${S}.mb-landscape .bb2-both{font-size:${BOTH_SCALE}em}
${S}.mb-landscape .bb2-both .bb2-photo{width:${PHOTO_BOTH}em}
`,
};
